import type { ReactNode } from "react";
import type { StorySectionKey } from "@/lib/orgStory";
import { STORY_SECTION_LABELS, storySectionAnchor } from "./storyLabels";

interface StorySectionProps {
  section: StorySectionKey;
  children: ReactNode;
  /** Skip the eyebrow/title — for sections whose component draws its own heading (Team, Reviews, News). */
  bare?: boolean;
  /** Kept mounted but out of view (a self-fetching section that came back empty). */
  hidden?: boolean;
}

/**
 * One story section: an anchor the pills scroll to, an eyebrow and a title,
 * then the section's existing cards. Not itself a card — the reused
 * components draw their own (spec 2026-09-24 §6.3, amended in PR 3).
 *
 * `tabIndex={-1}` lets `SectionPills` move focus here after a click, without
 * adding the section to the normal Tab order. `aria-labelledby` points at the
 * rendered heading so its accessible name matches what's on screen; `bare`
 * sections (which draw their own heading elsewhere) fall back to `aria-label`.
 */
export default function StorySection({ section, children, bare = false, hidden = false }: StorySectionProps) {
  const label = STORY_SECTION_LABELS[section];
  const anchor = storySectionAnchor(section);
  const titleId = `${anchor}-title`;
  return (
    <section
      id={anchor}
      data-story-section={section}
      tabIndex={-1}
      aria-label={bare ? label.pill : undefined}
      aria-labelledby={bare ? undefined : titleId}
      hidden={hidden}
      className="scroll-mt-[133px] outline-none"
    >
      {!bare && (
        <header className="mb-4">
          <p className="eyebrow text-[11px] text-gold-ink">{label.eyebrow}</p>
          <h2 id={titleId} className="mt-1 font-heading text-xl font-bold tracking-tight text-ink-900">
            {label.title}
          </h2>
        </header>
      )}
      <div className="flex flex-col gap-[22px]">{children}</div>
    </section>
  );
}
